import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export type DateValue = Date | string | number | null | undefined;

/**
 * 補上常見的非 ISO 日期字串格式，避免 dayjs 退回環境相依的原生解析行為
 */
const STRUCTURED_DATE_FORMATS = [
    'YYYY-M-D',
    'YYYY-M-D H',
    'YYYY-M-D H:m',
    'YYYY-M-D H:m:s',
    'YYYY/M/D',
    'YYYY/M/D H',
    'YYYY/M/D H:m',
    'YYYY/M/D H:m:s',
    'YYYY.M.D',
    'YYYY.M.D H',
    'YYYY.M.D H:m',
    'YYYY.M.D H:m:s',
];

/**
 * 解析日期輸入並回傳可安全操作的 dayjs 實例，解析失敗時回傳 null
 */
export const resolveDateInstance = (value: DateValue): Dayjs | null => {
    if (value instanceof Date || typeof value === 'number') {
        const parsedDate = dayjs(value);
        return parsedDate.isValid() ? parsedDate : null;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return null;
    }

    const structuredDate = dayjs(trimmedValue, STRUCTURED_DATE_FORMATS, true);
    if (structuredDate.isValid()) {
        return structuredDate;
    }

    const fallbackDate = dayjs(trimmedValue);
    return fallbackDate.isValid() ? fallbackDate : null;
};

/**
 * 判斷日期物件是否有效
 */
export const isValidDate = (value: Date): boolean => {
    return resolveDateInstance(value) !== null;
};

/**
 * 將日期輸入轉換為時間戳記，供排序或比較使用
 */
export const resolveDateTimestamp = (value: DateValue): number | null => {
    return resolveDateInstance(value)?.valueOf() ?? null;
};

/**
 * 將日期轉換為指定格式，解析失敗時可回傳指定 fallback
 */
export const formatDate = (
    date: DateValue,
    fmt: string,
    fallback?: string
): string => {
    const parsedDate = resolveDateInstance(date);
    if (parsedDate) {
        return parsedDate.format(fmt);
    }

    if (fallback !== undefined) {
        return fallback;
    }

    return dayjs(date as Date | string | number).format(fmt);
};

/**
 * 新增秒數
 */
export const addSeconds = (
    date: Date | string | number,
    seconds: number
): Dayjs => {
    return dayjs(date).add(seconds, 'second');
};

/**
 * 新增分鐘數
 */
export const addMinutes = (
    date: Date | string | number,
    minutes: number
): Dayjs => {
    return dayjs(date).add(minutes, 'minute');
};

/**
 * 新增小時數
 */
export const addHours = (
    date: Date | string | number,
    hours: number
): Dayjs => {
    return dayjs(date).add(hours, 'hour');
};

/**
 * 新增天數
 */
export const addDays = (date: Date | string | number, days: number): Dayjs => {
    return dayjs(date).add(days, 'day');
};

/**
 * 新增月份數
 */
export const addMonths = (
    date: Date | string | number,
    months: number
): Dayjs => {
    return dayjs(date).add(months, 'month');
};

/**
 * 新增年份數
 */
export const addYears = (
    date: Date | string | number,
    years: number
): Dayjs => {
    return dayjs(date).add(years, 'year');
};
